import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Typography,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Chip,
  IconButton,
  Grid,
  Alert,
  Stack,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Login as LoginIcon,
  Logout as LogoutIcon,
  FileDownload as FileDownloadIcon,
  Fullscreen as FullscreenIcon,
} from '@mui/icons-material';
import { format, subDays } from 'date-fns';
import {
  getEmployees,
  getAttendanceRecords,
  createAttendanceRecord,
  deleteAttendanceRecord,
  exportAttendanceCsv,
} from '../services/api';
import { useKioskTerminalLauncher } from '../hooks/useKioskTerminalLauncher';

const kindLabel = (kind) => (kind === 'entrada' ? 'Entrada' : 'Salida');

const sourceLabel = (source) => {
  if (source === 'kiosk') return 'Tablet';
  return 'Oficina';
};

const Attendance = () => {
  const { requestOpenTerminal, authDialog: kioskAuthDialog } = useKioskTerminalLauncher();
  const [employees, setEmployees] = useState([]);
  const [records, setRecords] = useState([]);
  const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [openDialog, setOpenDialog] = useState(false);
  const [formData, setFormData] = useState({
    employeeId: '',
    kind: 'entrada',
  });

  useEffect(() => {
    getEmployees()
      .then(({ data }) => setEmployees(data))
      .catch(console.error);
  }, []);

  useEffect(() => {
    loadRecords();
  }, [selectedDate]);

  const loadRecords = async () => {
    try {
      const { data } = await getAttendanceRecords({
        startDate: selectedDate,
        endDate: selectedDate,
        limit: 1000,
      });
      setRecords(data);
    } catch (error) {
      console.error('Error al cargar asistencia:', error);
      setRecords([]);
    }
  };

  const summary = useMemo(() => {
    const entradas = records.filter((r) => r.kind === 'entrada').length;
    const salidas = records.filter((r) => r.kind === 'salida').length;
    const employeeIds = new Set(records.map((r) => r.employee?._id || r.employee).filter(Boolean));
    return { entradas, salidas, empleados: employeeIds.size };
  }, [records]);

  const handleOpenDialog = () => {
    setFormData({ employeeId: '', kind: 'entrada' });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => setOpenDialog(false);

  const handleSubmit = async () => {
    if (!formData.employeeId) return;
    try {
      const emp = employees.find((e) => e._id === formData.employeeId);
      if (!emp?.employeeNumber) {
        alert('El empleado no tiene número asignado. Agrégalo en su expediente.');
        return;
      }
      await createAttendanceRecord({
        employeeNumber: emp.employeeNumber,
        kind: formData.kind,
      });
      handleCloseDialog();
      loadRecords();
    } catch (error) {
      alert(error.response?.data?.message || 'No se pudo registrar la asistencia');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este registro de asistencia?')) return;
    try {
      await deleteAttendanceRecord(id);
      loadRecords();
    } catch (error) {
      alert('No se pudo eliminar el registro');
    }
  };

  const handleExportCsv = async () => {
    const endDate = selectedDate;
    const startDate = format(subDays(new Date(selectedDate), 30), 'yyyy-MM-dd');
    try {
      const res = await exportAttendanceCsv({ startDate, endDate });
      const blob =
        res.data instanceof Blob
          ? res.data
          : new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `asistencia_${startDate}_${endDate}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      alert('No se pudo exportar el CSV');
    }
  };

  return (
    <Container maxWidth="lg">
      {kioskAuthDialog}

      <Box
        sx={{
          mb: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Typography variant="h4">Asistencia</Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Button
            variant="contained"
            color="secondary"
            startIcon={<FullscreenIcon />}
            onClick={requestOpenTerminal}
          >
            Pantalla tablet
          </Button>
          <Button variant="outlined" startIcon={<FileDownloadIcon />} onClick={handleExportCsv}>
            Exportar CSV
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenDialog}>
            Registrar entrada / salida
          </Button>
        </Stack>
      </Box>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4} md={3}>
            <TextField
              fullWidth
              type="date"
              label="Fecha"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={8} md={9}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip label={`${summary.empleados} empleados`} color="primary" variant="outlined" />
              <Chip label={`${summary.entradas} entradas`} color="success" variant="outlined" />
              <Chip label={`${summary.salidas} salidas`} color="info" variant="outlined" />
              <Chip label={`${records.length} registros`} variant="outlined" />
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Hora</TableCell>
              <TableCell>Tipo</TableCell>
              <TableCell>Nº emp.</TableCell>
              <TableCell>Empleado</TableCell>
              <TableCell>Sucursal</TableCell>
              <TableCell>Origen</TableCell>
              <TableCell align="right">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {records.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7}>
                  <Typography variant="body2" color="text.secondary">
                    Sin registros en esta fecha. Usa «Registrar entrada / salida» o la pantalla tablet.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              records.map((row) => (
                <TableRow key={row._id} hover>
                  <TableCell>{format(new Date(row.punchedAt), 'HH:mm:ss')}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={kindLabel(row.kind)}
                      color={row.kind === 'entrada' ? 'success' : 'primary'}
                      icon={row.kind === 'entrada' ? <LoginIcon /> : <LogoutIcon />}
                    />
                  </TableCell>
                  <TableCell>{row.employee?.employeeNumber || '—'}</TableCell>
                  <TableCell>
                    {row.employee
                      ? `${row.employee.firstName} ${row.employee.lastName}`
                      : '—'}
                  </TableCell>
                  <TableCell>{row.employee?.branch?.name || '—'}</TableCell>
                  <TableCell>
                    <Chip size="small" label={sourceLabel(row.source)} variant="outlined" />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" color="error" onClick={() => handleDelete(row._id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Registrar asistencia</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              select
              fullWidth
              label="Empleado"
              name="employeeId"
              value={formData.employeeId}
              onChange={(e) => setFormData({ ...formData, employeeId: e.target.value })}
            >
              {employees.map((emp) => (
                <MenuItem key={emp._id} value={emp._id}>
                  {emp.firstName} {emp.lastName}
                  {emp.employeeNumber ? ` (#${emp.employeeNumber})` : ' — sin número'}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              fullWidth
              label="Tipo"
              value={formData.kind}
              onChange={(e) => setFormData({ ...formData, kind: e.target.value })}
            >
              <MenuItem value="entrada">Entrada</MenuItem>
              <MenuItem value="salida">Salida</MenuItem>
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={!formData.employeeId}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Attendance;
